import React from 'react';
import { Box, Container, Grid, Typography, Paper, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ChatIcon from '@mui/icons-material/Chat';
import LockIcon from '@mui/icons-material/Lock';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SectionHeader from '../SectionHeader';
import StoreButtons from '../StoreButtons';

const SolutionSection = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const benefits = [
    {
      icon: <ChatIcon fontSize="large" sx={{ color: 'primary.main' }} />,
      title: 'Защищенный чат',
      description: 'Групповой чат врачей с пациентами и родственниками без раскрытия личных контактных данных.',
    },
    {
      icon: <LockIcon fontSize="large" sx={{ color: 'primary.main' }} />,
      title: 'Безопасность данных',
      description: 'Возможность размещения сервера хранения внутри инфраструктуры клиники.',
    },
    {
      icon: <AssignmentIcon fontSize="large" sx={{ color: 'primary.main' }} />,
      title: 'Трекер задач',
      description: 'Врачи вносят рекомендации и назначения в профиль пациента.',
    },
    {
      icon: <NotificationsActiveIcon fontSize="large" sx={{ color: 'primary.main' }} />,
      title: 'Умные уведомления',
      description: 'Система автоматически напоминает о запланированных процедурах.',
    }
  ];

  return (
    <Box
      id="solution"
      sx={{
        py: 10,
        backgroundColor: '#f5f9ff',
      }}
      ref={ref}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Наше решение"
          subtitle="ДокМедЧат — защищенный мессенджер для клиник с трекером задач для эффективной коммуникации."
        />

        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={10}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.7 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  }}
                >
                  <Typography variant="h5" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
                    Универсальное решение для медицинской коммуникации
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 2 }}>
                    ДокМедЧат — комплексное решение проблем коммуникации в медицине. Приложение доступно из AppStore и Google Play, а клиника может установить собственный сервер для данных.
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 3 }}>
                    Решение соответствует требованиям конфиденциальности медицинской информации и защиты личных данных.
                  </Typography>
                  <StoreButtons />
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={3}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(46, 80, 119, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {benefit.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
              Преимущество нашего решения
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', fontSize: '1.1rem' }}>
              В отличие от зарубежных решений, часто недоступных в России, ДокМедЧат предлагает аналогичную функциональность по доступной цене, адаптированную для российского рынка.
            </Typography>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default SolutionSection; 